import { ChatHeaderActionCluster } from "./ChatHeaderActionCluster";
import { type ThreadId, type WorkerRole } from "@cmmd-forge/contracts";
import { memo, type ReactNode, useMemo } from "react";
import { CornerUpLeftIcon } from "lucide-react";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { buildChatHeaderIdentity } from "./ChatHeader.logic";
import { WorkerRoleBadge } from "../workbar/WorkerRoleBadge";
import type { ComponentProps } from "react";
import {
  hiddenSurfaceCount,
  rightSidebarToggleLabel as rightSidebarToggleLabelFor,
  terminalToggleTooltip,
} from "./chatHeaderBadges";

/**
 * Thread action strip.
 *
 * It used to own identity too — thread title and project chip — but the global
 * breadcrumb names the route now, and this row sat directly beneath it saying
 * the same thing. What is left is what the bar cannot say: the jump-to-parent
 * link, the workspace context card, the goal and worker chips, and the surface
 * toggles on the right.
 */
/** Who the thread is: its lineage and the badge that marks it. */
interface ChatHeaderIdentityProps {
  readonly kind?: "master" | "worker" | null;
  readonly workerRole?: WorkerRole | null;
  readonly parentId?: ThreadId | null;
  readonly parentTitle?: string | null;
  readonly workerCount?: number;
}

/** Which surfaces are open, and how much unseen work each is hiding. */
interface ChatHeaderPanelProps {
  /** True iff the active thread is backed by a server session. */
  readonly isServerThread?: boolean;
  /** True when the current route can render a right sidebar surface. */
  readonly canOpenRightSidebar?: boolean;
  readonly previewOpen?: boolean;
  readonly canvasOpen?: boolean;
  readonly diffOpen?: boolean;
  readonly planOpen?: boolean;
  readonly terminalOpen?: boolean;
  readonly terminalRunningCount?: number;
  /** Workers still doing live work, badged onto the right-panel toggle. */
  readonly activeAgentCount?: number;
  readonly showPanelLauncher?: boolean;
}

/**
 * Grouped rather than listed flat.
 *
 * Thirty-five separate props said nothing about which belonged together, and
 * most were pure pass-throughs to one sub-component each — so every one of them
 * was a reason to change this signature. Four groups, each owned by the piece
 * that reads it.
 */
interface ChatHeaderProps {
  readonly runtimeControlsDisabledReason?: string | null;
  identity?: ChatHeaderIdentityProps;
  panels?: ChatHeaderPanelProps;
  projectActions?: ReactNode;
  onJumpToParent?: () => void;
  onTogglePreview?: () => void;
  onToggleTerminal?: () => void;
  jumpToParentShortcutLabel?: string | null;
  terminalShortcutLabel?: string | null;
}

export const ChatHeader = memo(function ChatHeader({
  runtimeControlsDisabledReason = null,
  identity: identityProps,
  panels,
  projectActions,
  onJumpToParent,
  onTogglePreview,
  onToggleTerminal,
  jumpToParentShortcutLabel,
  terminalShortcutLabel,
}: ChatHeaderProps) {
  const {
    isServerThread = false,
    canOpenRightSidebar,
    previewOpen = false,
    canvasOpen = false,
    diffOpen = false,
    planOpen = false,
    terminalOpen = false,
    terminalRunningCount = 0,
    activeAgentCount = 0,
    showPanelLauncher = true,
  } = panels ?? {};
  const identity = useChatHeaderIdentity(identityProps);

  const canUseRightSidebar = canOpenRightSidebar ?? isServerThread;
  const hasActiveRightSidebarPanel = previewOpen || canvasOpen || diffOpen || planOpen;
  // Same rule as the terminal badge: a count is only worth showing while the
  // surface that explains it is hidden. With the sidebar open the toggle means
  // "close", and badging a close button reads as an error rather than progress.
  const hiddenActiveAgentCount = hiddenSurfaceCount(hasActiveRightSidebarPanel, activeAgentCount);
  const rightSidebarToggleLabel = rightSidebarToggleLabelFor({
    panelOpen: hasActiveRightSidebarPanel,
    hiddenAgentCount: hiddenActiveAgentCount,
  });
  const hiddenRunningTerminalCount = hiddenSurfaceCount(terminalOpen, terminalRunningCount);
  const terminalTooltip = terminalToggleTooltip({
    hiddenRunningCount: hiddenRunningTerminalCount,
    shortcutLabel: terminalShortcutLabel,
  });
  const parentBreadcrumb = (
    <JumpToParentThreadLink
      breadcrumb={identity.parentBreadcrumb}
      onJumpToParent={onJumpToParent}
      shortcutLabel={jumpToParentShortcutLabel}
    />
  );

  return (
    /* One row, never wrapping: this used to be its own full-width header where
       controls could reflow onto a second line, and it now lives in the global
       bar's right zone (56px on touch, 40px from `md`). A wrap there would be
       clipped rather than shown.

       No `@container` here. The bar sizes its action column to the strip's
       intrinsic width, and `container-type: inline-size` makes an element's
       contents stop contributing to that width — the column collapsed to 4px
       and the toggles hung off the right edge. The `header-actions` container
       the action controls query is opened on the bar root instead
       (`APP_TOP_BAR_CLASS`), which is `w-full` and so carries it safely. */
    <div className="flex min-w-0 items-center gap-1 sm:gap-1.5">
      {/* Thread context, not thread control. On a 320px bar the breadcrumb and
          the two panel toggles are what the user needs to see and press; parent
          link, workspace context, goal, and worker role are all things they can
          get to on a wider viewport or from the thread itself. Held back to `md`
          so the bar never has to choose between a legible crumb and a reachable
          control. */}
      <ChatHeaderThreadRow parentBreadcrumb={parentBreadcrumb} badge={identity.badge} />

      <ChatHeaderActionCluster
        disabledReason={runtimeControlsDisabledReason}
        canUseRightSidebar={canUseRightSidebar}
        hasActiveRightSidebarPanel={hasActiveRightSidebarPanel}
        hiddenActiveAgentCount={hiddenActiveAgentCount}
        hiddenRunningTerminalCount={hiddenRunningTerminalCount}
        onTogglePreview={onTogglePreview}
        onToggleTerminal={onToggleTerminal}
        projectActions={projectActions}
        rightSidebarToggleLabel={rightSidebarToggleLabel}
        terminalOpen={terminalOpen}
        terminalTooltip={terminalTooltip}
        showPanelLauncher={showPanelLauncher}
      />
    </div>
  );
});

/**
 * The header's right-aligned entry points: project actions, the terminal
 * drawer, and the right sidebar.
 *
 * Rendered only while the sidebar is collapsed — once it is open the sidebar
 * owns its own tab switcher, and a second way in would compete with it. The
 * emptiness check lives here so `ChatHeader` does not have to ask whether any
 * of three independent things exist before laying out a row for them.
 */

/** The terminal drawer's toggle, badged while the drawer is hidden. */
/** Marks a thread that executes tasks directed by a lead. */
function WorkerThreadBadge({
  badge,
}: {
  readonly badge: ReturnType<typeof buildChatHeaderIdentity>["badge"];
}) {
  if (badge?.kind !== "worker") return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          // `no-drag-region` because this is the one control in the bar's
          // right zone that is not a button: a focusable `span` whose whole
          // job is to be hovered. The bar is the frameless window's drag
          // surface, and `index.css` exempts only button/a/input/textarea/
          // select, so without this the tooltip never opens.
          <span
            className="no-drag-region flex min-h-11 shrink-0 cursor-default items-center gap-1 sm:min-h-0"
            data-testid="chatheader-worker-badge"
            tabIndex={0}
          >
            <WorkerRoleBadge role={badge.workerRole} />
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              WORKER
            </span>
          </span>
        }
      />
      <TooltipPopup side="bottom">Worker thread — executes tasks directed by the lead</TooltipPopup>
    </Tooltip>
  );
}

/**
 * The checkout context, behind a hover.
 *
 * Rendered only when there is something to say — a popover that opens onto
 * three empty rows is worse than no affordance at all.
 */

/**
 * A link back to the thread that spawned this one.
 *
 * Only drawn when there is both a parent and somewhere to go: a control that
 * names a parent but cannot reach it is dead, which reads as broken rather than
 * as absent.
 *
 * Deliberately not called a breadcrumb. `check-global-breadcrumb` reserves that
 * word for the one bar that answers "where does this route sit", and this
 * answers a different question — "the thread this one was spawned from" — which
 * is why the guard names lowercase `parentBreadcrumb` as the thing it is not
 * about.
 */
function JumpToParentThreadLink({
  breadcrumb,
  onJumpToParent,
  shortcutLabel,
}: {
  readonly breadcrumb: ReturnType<typeof buildChatHeaderIdentity>["parentBreadcrumb"];
  readonly onJumpToParent: (() => void) | undefined;
  readonly shortcutLabel: string | null | undefined;
}) {
  if (!breadcrumb || !onJumpToParent) return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            onClick={onJumpToParent}
            className="flex min-h-11 shrink-0 items-center gap-1 rounded-lg px-1.5 text-[11px] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:min-h-0 sm:rounded-sm sm:px-0"
            data-testid="chatheader-parent-breadcrumb"
          >
            <CornerUpLeftIcon aria-hidden="true" className="size-3 shrink-0" />
            <span className="max-w-[160px] truncate font-medium text-foreground/80">
              {breadcrumb.label}
            </span>
            <span aria-hidden="true" className="shrink-0 text-muted-foreground/60">
              /
            </span>
          </button>
        }
      />
      <TooltipPopup side="bottom">
        {shortcutLabel ? `Jump to parent thread (${shortcutLabel})` : "Jump to parent thread"}
      </TooltipPopup>
    </Tooltip>
  );
}

/**
 * The thread's goal, once there is a server thread to hang it on.
 *
 * A draft has no thread id, and the chip's own actions address the thread — so
 * showing it before promotion would offer edits with nowhere to land.
 */

/**
 * What kind of thread this is, and what it descends from.
 *
 * Every field arrives optional and is normalised to `null` before the builder
 * sees it, because absent and null mean the same thing here — there is no
 * parent, or no worker role — and letting both through would give the builder
 * two ways to say one thing. `workerCount` is the exception: it is spread only
 * when present, since zero workers and an unknown count are different answers.
 */
function useChatHeaderIdentity(input: ChatHeaderIdentityProps | undefined) {
  const {
    kind: activeThreadKind,
    workerRole: activeThreadWorkerRole,
    parentId: activeThreadParentId,
    parentTitle: activeThreadParentTitle,
    workerCount: activeThreadWorkerCount,
  } = input ?? {};

  return useMemo(
    () =>
      buildChatHeaderIdentity({
        kind: activeThreadKind ?? null,
        workerRole: activeThreadWorkerRole ?? null,
        parentThreadId: activeThreadParentId ?? null,
        parentTitle: activeThreadParentTitle ?? null,
        ...(activeThreadWorkerCount !== undefined ? { workerCount: activeThreadWorkerCount } : {}),
      }),
    [
      activeThreadKind,
      activeThreadWorkerRole,
      activeThreadParentId,
      activeThreadParentTitle,
      activeThreadWorkerCount,
    ],
  );
}

/**
 * What the header says about the thread itself, on wide screens.
 *
 * No title and no project chip: the global breadcrumb owns `project / thread`
 * now, and repeating it here read as the same fact stated twice. What the
 * title's tooltip carried — folder, worktree, branch, PR — is not in the
 * breadcrumb, so it keeps its own affordance rather than being lost with the
 * heading it happened to hang off.
 */
function ChatHeaderThreadRow({
  parentBreadcrumb,
  badge,
}: {
  readonly parentBreadcrumb: ReactNode;
  readonly badge: ComponentProps<typeof WorkerThreadBadge>["badge"];
}) {
  return (
    <div className="hidden min-w-0 items-center gap-1 sm:gap-1.5 md:flex">
      {parentBreadcrumb}
      {/* No title, no project chip: the global breadcrumb owns
          `project / thread` now, and repeating it here read as the same fact
          stated twice. What the title's tooltip carried — folder, worktree,
          branch, PR — is not in the breadcrumb, so it keeps its own affordance
          rather than being lost with the heading it happened to hang off. */}
      <WorkerThreadBadge badge={badge} />
    </div>
  );
}
